import React, { useState, useEffect } from 'react';
import { Search, AlertCircle, Info, AlertTriangle, XCircle, RefreshCw, Download } from 'lucide-react';
import { getAuthHeaders } from '../../utils/authUtils';

const LogViewer = ({ authenticatedFetch }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [filter, setFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [error, setError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  
  const API_BASE = `${process.env.REACT_APP_API_BASE_URL}/api/logs`;
  
  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    setDebugInfo(null);
    try {
      let url = API_BASE;
      if (filter !== 'ALL') {
        url = `${API_BASE}/level/${filter}`;
      }
      url += '?lines=500';
      
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch logs (Status: ${response.status})`);
      }

      const data = await response.json();
      setLogs(data);
      
      if (data.length === 0) {
        setDebugInfo({
          message: 'No logs parsed from file. This might mean:',
          details: [
            '1. Log file is empty or not accessible',
            '2. Log parsing regex doesn\'t match the log format',
            '3. Check browser console for more details'
          ]
        });
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError(err.message);
    }
    setLoading(false);
  };
  
  const searchLogs = async () => {
    if (!searchTerm) {
      fetchLogs();
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `${API_BASE}/search?keyword=${encodeURIComponent(searchTerm)}&maxResults=500`,
        { method: 'GET', headers: getAuthHeaders(), }
      );

      if (!response.ok) {
        throw new Error('Failed to search logs');
      }

      const data = await response.json();
      setLogs(data);
    } catch (err) {
      console.error('Error searching logs:', err);
      setError(err.message);
    }
    setLoading(false);
  };

  const downloadLogs = async (downloadType = 'all', startDate = null, endDate = null) => {
    setDownloading(true);
    setDownloadError(null);
    try {
      let url = `${API_BASE}/download`;
      
      if (downloadType === 'level' && filter !== 'ALL') {
        url = `${API_BASE}/download/level/${filter}`;
      } else if (downloadType === 'range' && startDate && endDate) {
        url = `${API_BASE}/download/range?start=${encodeURIComponent(startDate)}&end=${encodeURIComponent(endDate)}`;
      }
      
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to download logs (${response.status})`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      let filename = 'application.log';
      const contentDisposition = response.headers.get('content-disposition');
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="([^"]+)"/);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Error downloading logs:', err);
      setDownloadError(err.message);
    }
    setDownloading(false);
  };

  const downloadFilteredLogs = async () => {
    if (filter !== 'ALL') {
      await downloadLogs('level');
    } else {
      await downloadLogs('all');
    }
  };

  const downloadDateRangeLogs = async () => {
    const startDate = prompt('Enter start date (YYYY-MM-DDTHH:mm:ss):');
    if (!startDate) return;
    
    const endDate = prompt('Enter end date (YYYY-MM-DDTHH:mm:ss):');
    if (!endDate) return;
    
    await downloadLogs('range', startDate, endDate);
  };
  
  useEffect(() => {
    fetchLogs();
  }, [filter]);
  
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(fetchLogs, 5000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, filter]);
  
  const getLevelIcon = (level) => {
    switch (level?.toUpperCase()) {
      case 'ERROR': return <XCircle className="text-danger" size={16} />;
      case 'WARN': return <AlertTriangle className="text-warning" size={16} />;
      case 'INFO': return <Info className="text-primary" size={16} />;
      case 'DEBUG': return <AlertCircle className="text-secondary" size={16} />;
      default: return <AlertCircle className="text-secondary" size={16} />;
    }
  };
  
  const getLevelBadgeClass = (level) => {
    switch (level?.toUpperCase()) {
      case 'ERROR': return 'badge bg-danger';
      case 'WARN': return 'badge bg-warning text-dark';
      case 'INFO': return 'badge bg-primary';
      case 'DEBUG': return 'badge bg-secondary';
      default: return 'badge bg-light text-dark';
    }
  };
  
  const getLogCardClass = (level) => {
    switch (level?.toUpperCase()) {
      case 'ERROR': return 'border-danger';
      case 'WARN': return 'border-warning';
      case 'INFO': return 'border-primary';
      case 'DEBUG': return 'border-secondary';
      default: return '';
    }
  };
  
  return (
    <div className="container-fluid py-4">
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h2 className="card-title mb-4">Application Logs</h2>
          
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-auto">
              <div className="btn-group" role="group">
                {['ALL', 'ERROR', 'WARN', 'INFO', 'DEBUG'].map((level) => (
                  <button
                    key={level}
                    type="button"
                    onClick={() => setFilter(level)}
                    className={`btn ${filter === level ? 'btn-primary' : 'btn-outline-primary'}`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="col-12 col-md">
              <div className="input-group">
                <span className="input-group-text">
                  <Search size={16} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchLogs()}
                />
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={searchLogs}
                >
                  Search
                </button>
              </div>
            </div>
            
            <div className="col-12 col-md-auto">
              <div className="btn-group" role="group">
                <button
                  className="btn btn-success"
                  onClick={fetchLogs}
                  disabled={loading}
                  title="Refresh logs"
                >
                  <RefreshCw size={16} className={loading ? 'spinner-border spinner-border-sm' : ''} />
                  {' '}Refresh
                </button>
                
                <button
                  className={`btn ${autoRefresh ? 'btn-success' : 'btn-outline-success'}`}
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  title="Toggle auto-refresh every 5 seconds"
                >
                  Auto {autoRefresh ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>
          </div>

          <div className="row g-2 mb-3">
            <div className="col-12 col-md-auto">
              <div className="btn-group" role="group">
                <button
                  className="btn btn-outline-success"
                  onClick={() => downloadLogs('all')}
                  disabled={downloading}
                  title="Download complete application log"
                >
                  <Download size={16} />
                  {' '}Download All
                </button>
                
                <button
                  className="btn btn-outline-success"
                  onClick={downloadFilteredLogs}
                  disabled={downloading}
                  title={filter !== 'ALL' ? `Download ${filter} level logs` : 'Select a log level first'}
                >
                  <Download size={16} />
                  {' '}Download {filter !== 'ALL' ? filter : 'Filtered'}
                </button>
                
                <button
                  className="btn btn-outline-success"
                  onClick={downloadDateRangeLogs}
                  disabled={downloading}
                  title="Download logs by date range"
                >
                  <Download size={16} />
                  {' '}Download by Range
                </button>
              </div>
            </div>
          </div>
          
          <div className="text-muted small mb-2">
            Showing {logs.length} log entries
          </div>
        </div>
      </div>
      
      {error && (
        <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
          <AlertTriangle size={20} className="me-2" />
          <div>
            <strong>Error loading logs:</strong> {error}
          </div>
        </div>
      )}

      {downloadError && (
        <div className="alert alert-warning d-flex align-items-center mb-4" role="alert">
          <AlertTriangle size={20} className="me-2" />
          <div>
            <strong>Download error:</strong> {downloadError}
          </div>
        </div>
      )}

      {debugInfo && (
        <div className="alert alert-info d-flex align-items-start mb-4" role="alert">
          <AlertCircle size={20} className="me-2 mt-1" style={{ flexShrink: 0 }} />
          <div>
            <strong>{debugInfo.message}</strong>
            <ul className="mb-0 mt-2 ms-3">
              {debugInfo.details.map((detail, idx) => (
                <li key={idx} className="small">{detail}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {downloading && (
        <div className="alert alert-info d-flex align-items-center mb-4" role="alert">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Downloading...</span>
          </div>
          <div>
            <strong>Downloading logs...</strong> This may take a moment.
          </div>
        </div>
      )}
      
      <div className="row g-3">
        {logs.map((log, index) => (
          <div key={index} className="col-12">
            <div className={`card border-start border-3 ${getLogCardClass(log.level)}`}>
              <div className="card-body">
                <div className="d-flex align-items-start">
                  <div className="me-3 mt-1">
                    {getLevelIcon(log.level)}
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                      <span className={getLevelBadgeClass(log.level)}>
                        {log.level}
                      </span>
                      <span className="text-muted small" title={log.timestamp}>
                        {log.timestamp}
                      </span>
                      <span className="badge bg-light text-dark font-monospace small" title={`Thread: ${log.thread}`}>
                        {log.thread}
                      </span>
                      {log.traceId && log.traceId !== 'N/A' && (
                        <span className="badge bg-info font-monospace small" title={`Trace ID: ${log.traceId}`}>
                          tid: {log.traceId}
                        </span>
                      )}
                    </div>
                    <div className="font-monospace small text-muted mb-1">
                      {log.logger}
                    </div>
                    <div className="text-break" style={{ wordBreak: 'break-word' }}>
                      {log.message}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {logs.length === 0 && !loading && !error && (
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center py-5">
                <AlertCircle size={48} className="text-muted mb-3" />
                <p className="text-muted">No logs found</p>
                <p className="text-muted small">
                  Try increasing the lines parameter or check if logs are being generated
                </p>
              </div>
            </div>
          </div>
        )}
        
        {loading && (
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted">Loading logs...</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogViewer;